import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  addDoc, 
  query,
  orderBy,
  limit,
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { populateCourses } from '../../utils/populateCourses';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowRightOnRectangleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInquiries, setLoadingInquiries] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    duration: '',
    price: '',
    description: '',
    content: [],
    featured: false,
  });

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/admin');
    } else {
      fetchCourses();
      fetchInquiries();
    }
  }, [user, isAdmin, navigate]);

  const fetchCourses = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'courses'));
      const coursesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCourses(coursesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setLoading(false);
    }
  };

  const fetchInquiries = async () => {
    try {
      const q = query(
        collection(db, 'inquiries'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setInquiries(data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoadingInquiries(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const openModal = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        name: course.name,
        duration: course.duration,
        price: course.price,
        description: course.description,
        content: course.content || [],
        featured: course.featured || false,
      });
    } else {
      setEditingCourse(null);
      setFormData({
        name: '',
        duration: '',
        price: '',
        description: '',
        content: [],
        featured: false,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCourse(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleContentChange = (index, value) => {
    const newContent = [...formData.content];
    newContent[index] = value;
    setFormData({ ...formData, content: newContent });
  };

  const addContentField = () => {
    setFormData({ ...formData, content: [...formData.content, ''] });
  };

  const removeContentField = (index) => {
    const newContent = formData.content.filter((_, i) => i !== index);
    setFormData({ ...formData, content: newContent });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await updateDoc(doc(db, 'courses', editingCourse.id), {
          ...formData,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'courses'), {
          ...formData,
          createdAt: serverTimestamp(),
        });
      }
      fetchCourses();
      closeModal();
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Failed to save course. Please try again.');
    }
  };

  const handleDelete = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await deleteDoc(doc(db, 'courses', courseId));
        fetchCourses();
      } catch (error) {
        console.error('Error deleting course:', error);
        alert('Failed to delete course. Please try again.');
      }
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (window.confirm('Delete this inquiry?')) {
      try {
        await deleteDoc(doc(db, 'inquiries', inquiryId));
        fetchInquiries();
      } catch (error) {
        console.error('Error deleting inquiry:', error);
        alert('Failed to delete inquiry. Please try again.');
      }
    }
  };

  const handleMarkInquiry = async (inquiryId, nextStatus) => {
    try {
      await updateDoc(doc(db, 'inquiries', inquiryId), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });
      fetchInquiries();
    } catch (error) {
      console.error('Error updating inquiry:', error);
      alert('Failed to update inquiry. Please try again.');
    }
  };

  const handlePopulateCourses = async () => {
    if (window.confirm('This will add the default courses to the database. Continue?')) {
      try {
        await populateCourses();
        fetchCourses();
        alert('Courses populated successfully!');
      } catch (error) {
        console.error('Error populating courses:', error);
        alert('Failed to populate courses. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Welcome, {user?.displayName || user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Manage Courses</h2>
          <div className="flex gap-3">
            {courses.length === 0 && (
              <button
                onClick={handlePopulateCourses}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Populate Default Courses
              </button>
            )}
            <button
              onClick={() => openModal()}
              className="inline-flex items-center gap-2 rounded-md bg-gold-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gold-500"
            >
              <PlusIcon className="h-5 w-5" />
              Add Course
            </button>
          </div>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <div key={course.id} className="bg-white rounded-lg shadow p-6">
              {course.featured && (
                <span className="inline-block mb-2 rounded-full bg-gold-600 px-3 py-1 text-xs font-semibold text-white">
                  Featured
                </span>
              )}
              <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name}</h3>
              <p className="text-sm text-gray-600 mb-2">{course.duration}</p>
              <p className="text-2xl font-bold text-gold-600 mb-4">{course.price}</p>
              <p className="text-sm text-gray-700 mb-4">{course.description}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => openModal(course)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-200"
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(course.id)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-red-100 px-3 py-2 text-sm font-semibold text-red-900 hover:bg-red-200"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {courses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No courses yet. Click "Add Course" to create one.</p>
          </div>
        )}

        {/* Inquiries */}
        <div className="mt-16 border-t border-gray-200 pt-16">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Contact Inquiries</h2>
              <p className="text-sm text-gray-600 mt-1">
                {inquiries.length > 0 && (
                  <>
                    {inquiries.filter(i => i.status === 'new').length} new, {inquiries.filter(i => i.status === 'in_progress').length} in progress, {inquiries.filter(i => i.status === 'resolved').length} resolved
                  </>
                )}
              </p>
            </div>
            <button
              onClick={() => {
                setLoadingInquiries(true);
                fetchInquiries();
              }}
              className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>

          {loadingInquiries ? (
            <div className="py-8 text-gray-600">Loading inquiries…</div>
          ) : inquiries.length === 0 ? (
            <div className="py-8 text-gray-600">No inquiries yet.</div>
          ) : (
            <div className="mt-6 overflow-x-auto rounded-lg bg-white shadow ring-1 ring-gray-900/10">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">When</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Course</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inquiries.map((inq) => {
                    const createdAt =
                      inq.createdAt && typeof inq.createdAt.toDate === 'function'
                        ? inq.createdAt.toDate()
                        : null;
                    const when = createdAt ? createdAt.toLocaleString() : '-';
                    return (
                      <tr key={inq.id} className="align-top">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{when}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{inq.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {inq.email ? (
                            <a className="text-gold-700 hover:text-gold-600" href={`mailto:${inq.email}`}>
                              {inq.email}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {inq.phone ? (
                            <a className="text-gold-700 hover:text-gold-600" href={`tel:${inq.phone}`}>
                              {inq.phone}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{inq.course || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 min-w-[260px]">
                          <div className="line-clamp-4">{inq.message || '-'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              inq.status === 'resolved'
                                ? 'bg-green-100 text-green-800'
                                : inq.status === 'in_progress'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {inq.status || 'new'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          <div className="flex justify-end gap-2">
                            {inq.status !== 'resolved' && (
                              <button
                                onClick={() => handleMarkInquiry(inq.id, 'resolved')}
                                className="rounded-md bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700"
                              >
                                Mark Resolved
                              </button>
                            )}
                            {inq.status !== 'in_progress' && inq.status !== 'resolved' && (
                              <button
                                onClick={() => handleMarkInquiry(inq.id, 'in_progress')}
                                className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                              >
                                In Progress
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteInquiry(inq.id)}
                              className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-900 hover:bg-red-200"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingCourse ? 'Edit Course' : 'Add New Course'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Course Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gold-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Duration *
                  </label>
                  <input
                    type="text"
                    name="duration"
                    required
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="e.g., 1 Week"
                    className="block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gold-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Price *
                  </label>
                  <input
                    type="text"
                    name="price"
                    required
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="e.g., ₹45,000"
                    className="block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gold-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  required
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gold-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Course Content
                </label>
                {formData.content.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleContentChange(index, e.target.value)}
                      className="block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gold-600"
                    />
                    <button
                      type="button"
                      onClick={() => removeContentField(index)}
                      className="rounded-md bg-red-100 px-3 py-2 text-sm font-semibold text-red-900 hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addContentField}
                  className="mt-2 text-sm font-semibold text-gold-600 hover:text-gold-500"
                >
                  + Add Content Item
                </button>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="featured"
                  id="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-gold-600 focus:ring-gold-600"
                />
                <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                  Mark as featured course
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 rounded-md bg-gold-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gold-500"
                >
                  {editingCourse ? 'Update Course' : 'Create Course'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
